import { ChevronDown, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { isFailClosed } from '@/lib/agent-platform';

import {
  type AiDecision,
  type AuditTrailEntry,
  downloadAuditTrailPdf,
  useAuditTrail,
} from '../api/risk-assessment-wizard';
import { FailClosedNotice } from './fail-closed-notice';

interface AuditTrailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentId: string;
}

/**
 * Format ISO timestamp to full date and time format (DD/MM/YYYY HH:MM:SS)
 */
function formatTimestamp(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  return date.toLocaleString('en-GB', { hour12: false });
}

/** Header event types shown flat at the top */
const HEADER_EVENT_TYPES = new Set(['screening.initiated', 'input.received']);

/** Footer event types shown flat at the bottom */
const FOOTER_EVENT_TYPES = new Set([
  'screening.completed',
  'screening.failed',
  'screening.final',
  'output.generated',
  'risk.recalculated',
  'assessment.updated',
]);

/** Footer categories (fallback for older data without eventType) */
const FOOTER_CATEGORIES = new Set(['screening.decision', 'screening.output']);

function isHeaderEntry(entry: AuditTrailEntry): boolean {
  return HEADER_EVENT_TYPES.has(entry.eventType);
}

function isFooterEntry(entry: AuditTrailEntry): boolean {
  if (FOOTER_EVENT_TYPES.has(entry.eventType)) return true;
  if (FOOTER_CATEGORIES.has(entry.category)) return true;
  // Lifecycle events that are completion/failure
  if (entry.category === 'screening.lifecycle' && !HEADER_EVENT_TYPES.has(entry.eventType)) {
    return true;
  }
  return false;
}

interface GroupedEntries {
  header: AuditTrailEntry[];
  sections: { label: string; entries: AuditTrailEntry[] }[];
  investigationResults: AuditTrailEntry[];
}

/**
 * Map the eventType suffix of an ai.decision.* entry to the same section
 * label used by the corresponding check.completed summary, so the summary
 * row and the AI reasoning row land in the same accordion group.
 */
const AI_DECISION_TO_CHECK_LABEL: Record<string, string> = {
  'sanctions.match_prioritisation': 'Sanctions',
  'adverse_media.validation': 'Adverse media',
  'adverse_media.article_analysis': 'Adverse media',
  'pep.analysis': 'PEP',
  'pep.consolidation': 'PEP',
  'pep.snippet_validation': 'PEP',
  'companies_house.officer_match': 'Companies House',
  'business_risk.analysis': 'Business risk',
  'investigator.synthesis': 'Investigation results',
};

/**
 * Extract a human-readable check name from the entry.
 * - ai.decision.<group>.<sub> events → mapped via AI_DECISION_TO_CHECK_LABEL
 * - "X check completed/failed/skipped" messages → the "X" prefix
 * - sanctions entries → the specific list (UK / EU / UN / OFAC) pulled from the summary
 * Falls back to agentName if no pattern matches.
 *
 * Exported for unit testing.
 */
export function extractCheckLabel(entry: AuditTrailEntry): string {
  if (entry.eventType.startsWith('ai.decision.')) {
    const suffix = entry.eventType.replace(/^ai\.decision\./, '');
    if (suffix === 'sanctions.match_prioritisation' && entry.aiDecision?.agent) {
      // AI sanctions decisions are per-list; promote to the same label the
      // corresponding "X sanctions check completed" summary uses.
      const agent = entry.aiDecision.agent;
      if (agent.startsWith('uk-')) return 'UK sanctions';
      if (agent.startsWith('eu-')) return 'EU sanctions';
      if (agent.startsWith('un-')) return 'UN sanctions';
      if (agent.startsWith('ofac-')) return 'OFAC sanctions';
    }
    return AI_DECISION_TO_CHECK_LABEL[suffix] ?? entry.agentName;
  }

  const msg = entry.message;
  // Match patterns like "UK sanctions check", "PEP check", "Adverse media check", etc.
  const match = msg.match(/^(.+?)\s+check\s+(completed|failed|skipped)/i);
  if (match) {
    return match[1];
  }

  return entry.agentName;
}

function groupEntries(entries: AuditTrailEntry[]): GroupedEntries {
  const header: AuditTrailEntry[] = [];
  const investigationResults: AuditTrailEntry[] = [];
  const checkEntries: AuditTrailEntry[] = [];

  for (const entry of entries) {
    if (isHeaderEntry(entry)) {
      header.push(entry);
    } else if (entry.category === 'screening.check') {
      // All check summaries and ai.decision.* reasoning rows share this
      // category, so both end up in the same accordion group.
      checkEntries.push(entry);
    } else if (isFooterEntry(entry)) {
      investigationResults.push(entry);
    } else {
      // Default: treat unknown entries as check entries for grouping
      checkEntries.push(entry);
    }
  }

  // Group check entries by extracted check label, preserving order of first appearance
  const sectionMap = new Map<string, AuditTrailEntry[]>();
  for (const entry of checkEntries) {
    const key = extractCheckLabel(entry);
    if (!sectionMap.has(key)) {
      sectionMap.set(key, []);
    }
    sectionMap.get(key)!.push(entry);
  }

  const sections = Array.from(sectionMap.entries()).map(([label, sectionEntries]) => ({
    label,
    entries: sectionEntries,
  }));

  return { header, sections, investigationResults };
}

function formatScore(value?: number): string | null {
  if (typeof value !== 'number') return null;
  return value.toFixed(2);
}

/**
 * Collapsible block showing per-item AI reasoning for an audit entry.
 * Hidden by default — reviewers opt in when they need to audit the decision.
 *
 * Exported for unit testing.
 */
export function AiReasoningBlock({ aiDecision }: { aiDecision: AiDecision }) {
  const [isOpen, setIsOpen] = useState(false);
  const itemCount = aiDecision.items.length;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0077b6] hover:text-[#023e8a]"
      >
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
        {isOpen ? 'Hide' : 'Show'} AI reasoning
        {itemCount > 0 ? ` (${itemCount} item${itemCount === 1 ? '' : 's'})` : ''}
      </button>
      {isOpen && (
        <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3">
          <div className="mb-2 text-[12px] text-gray-600">
            <span className="font-medium">Decision:</span> {aiDecision.decision}
          </div>
          {aiDecision.aggregate && Object.keys(aiDecision.aggregate).length > 0 && (
            <div className="mb-2 text-[12px] text-gray-600">
              {Object.entries(aiDecision.aggregate)
                .map(([k, v]) => `${k}: ${typeof v === 'number' ? v.toFixed(2) : String(v)}`)
                .join(' · ')}
            </div>
          )}
          <ul className="space-y-2">
            {aiDecision.items.map((item, idx) => {
              const score = formatScore(item.score);
              const confidence = formatScore(item.confidence);
              const isUrl = /^https?:\/\//i.test(item.target);
              // target is unique per decision batch in practice; fall back to
              // index to avoid runtime collisions if it's ever repeated.
              const key = `${item.target}#${idx}`;
              return (
                <li key={key} className="rounded border border-gray-200 bg-white p-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-semibold text-[#051923]">
                      {item.targetLabel ?? item.target}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        item.outcome === 'KEPT' ||
                        item.outcome === 'PRIORITISED' ||
                        item.outcome === 'MATCHED'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {item.outcome}
                    </span>
                  </div>
                  {item.targetLabel && isUrl && (
                    <a
                      href={item.target}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-0.5 block truncate font-mono text-[11px] text-[#0077b6] hover:underline"
                    >
                      {item.target}
                    </a>
                  )}
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-600">
                    {score && (
                      <span>
                        <span className="font-medium">Score:</span> {score}
                      </span>
                    )}
                    {confidence && (
                      <span>
                        <span className="font-medium">Confidence:</span> {confidence}
                      </span>
                    )}
                    {item.flags && item.flags.length > 0 && (
                      <span className="flex flex-wrap gap-1">
                        {item.flags.map((flag) => (
                          <span
                            key={flag}
                            className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900"
                          >
                            {flag}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  {isFailClosed(item.reasoning) ? (
                    <FailClosedNotice fieldLabel="Reasoning" variant="inline" className="mt-1" />
                  ) : (
                    <p className="mt-1 text-[12px] leading-relaxed text-[#051923]">
                      {item.reasoning}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Individual audit entry row component.
 *
 * Exported for unit testing.
 */
export function AuditEntryRow({ entry }: { entry: AuditTrailEntry }) {
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between">
        <span className="text-[15px] font-semibold text-[#051923]">{entry.agentName}</span>
        <span className="font-mono text-[13px] text-gray-500">
          {formatTimestamp(entry.timestamp)}
        </span>
      </div>
      {isFailClosed(entry.message) ? (
        <FailClosedNotice fieldLabel="Audit message" variant="inline" className="mt-1" />
      ) : (
        <p className="mt-1 text-[14px] leading-relaxed text-[#051923]">{entry.message}</p>
      )}
      {entry.aiDecision && <AiReasoningBlock aiDecision={entry.aiDecision} />}
    </div>
  );
}

/**
 * Accordion section for a group of check entries
 */
function AccordionSection({
  label,
  entries,
  isExpanded,
  onToggle,
}: {
  label: string;
  entries: AuditTrailEntry[];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-3 text-left transition-colors hover:bg-gray-50"
      >
        <span className="text-[15px] font-semibold text-[#051923]">{label}</span>
        <ChevronDown
          className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
      {isExpanded && (
        <div className="px-3 pb-3">
          {entries.map((entry) => (
            <div key={entry.id} className="mb-2 last:mb-0">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[13px] text-gray-500">
                  {formatTimestamp(entry.timestamp)}
                </span>
              </div>
              {isFailClosed(entry.message) ? (
                <FailClosedNotice fieldLabel="Audit message" variant="inline" className="mt-0.5" />
              ) : (
                <p className="mt-0.5 text-[14px] leading-relaxed text-[#051923]">{entry.message}</p>
              )}
              {entry.aiDecision && <AiReasoningBlock aiDecision={entry.aiDecision} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AuditTrailModal({ open, onOpenChange, assessmentId }: AuditTrailModalProps) {
  const { data: auditTrail, isLoading, error } = useAuditTrail(assessmentId, open);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const grouped = useMemo(() => {
    if (!auditTrail?.entries.length) return null;
    return groupEntries(auditTrail.entries);
  }, [auditTrail?.entries]);

  const toggleSection = (key: string) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleExportPdf = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await downloadAuditTrailPdf(assessmentId);
      toast.success('Audit trail exported successfully');
    } catch {
      toast.error('Failed to export audit trail');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-[900px]">
        <DialogHeader className="flex flex-row items-center justify-between pr-10">
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            AI Audit Trail
          </DialogTitle>
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={isExporting || isLoading}
            className="text-[14px] font-medium text-[#131313] underline hover:text-[#131313]/70 disabled:opacity-50"
          >
            {isExporting ? 'Exporting...' : 'Export as PDF'}
          </button>
        </DialogHeader>

        <div className="mt-4 flex-1 overflow-y-auto pr-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="py-8 text-center text-red-600">Failed to load audit trail</div>
          ) : !grouped || auditTrail?.entries.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No audit trail entries found</div>
          ) : (
            <div>
              {/* Header entries — shown flat */}
              {grouped.header.length > 0 && (
                <div className="mb-4 space-y-1">
                  {grouped.header.map((entry) => (
                    <AuditEntryRow key={entry.id} entry={entry} />
                  ))}
                </div>
              )}

              {/* Check entries — grouped by check type in accordion sections */}
              {grouped.sections.length > 0 && (
                <div className="mb-4 rounded-md border border-gray-200">
                  {grouped.sections.map((section) => (
                    <AccordionSection
                      key={section.label}
                      label={section.label}
                      entries={section.entries}
                      isExpanded={expandedKeys.includes(section.label)}
                      onToggle={() => toggleSection(section.label)}
                    />
                  ))}
                </div>
              )}

              {/* Investigation results — shown flat */}
              {grouped.investigationResults.length > 0 && (
                <div className="mb-4 space-y-1">
                  {grouped.investigationResults.map((entry) => (
                    <AuditEntryRow key={entry.id} entry={entry} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
